import { KFONE_ROLE } from '@/models/enum';
import { axiosGetIDPConfig } from '@/modules/hiring/stateManagement/api';
import { eeEnvVariables } from '@/utilities/constants';
import { useMsal } from '@azure/msal-react';
import * as teams from '@microsoft/teams-js';
import { getDecodedB2cToken } from 'modules/auth/components/b2c/utils';
import { useEffect } from 'react';

const BotAuthStart = () => {
  const { instance: msalInstance } = useMsal();

  useEffect(() => {
    InitializeBotAuthStart();
  }, []);

  const InitializeBotAuthStart = async () => {
    await teams.app.initialize();
    const redirectResult = await msalInstance.handleRedirectPromise();
    if (redirectResult) {
      return sendUserDetailsToBot(redirectResult);
    }
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const silent = await msalInstance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email', 'User.Read'],
          account: accounts[0],
        });
        return sendUserDetailsToBot(silent);
      } catch (err) {
        console.warn('Silent login FAILED:', err);
      }
    }
    const ctx = await teams.app.getContext();
    const isDesktop = ctx.app.host.clientType === 'desktop';
    if (isDesktop) {
      return msalInstance.loginRedirect({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
        prompt: 'select_account',
        redirectUri: `${window.location.origin}/botstart`,
        authority: 'https://login.microsoftonline.com/common',
      });
    }
    return msalInstance
      .loginPopup({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
        prompt: 'select_account',
        redirectUri: `${window.location.origin}/botstart`,
        authority: 'https://login.microsoftonline.com/common',
      })
      .then(sendUserDetailsToBot)
      .catch((err: any) => {
        console.error('Popup login ERROR:', err);
        teams.authentication.notifyFailure(err.message || 'Login failed');
      });
  };

  const sendUserDetailsToBot = async (result: any) => {
    try {
      await teams.app.initialize();
      // Try to get Teams SSO token, but fallback to MSAL token if it fails
      let teamsToken: string | null = null;
      try {
        teamsToken = await new Promise<string>((resolve, reject) => {
          teams.authentication.getAuthToken({
            successCallback: (token: string) => resolve(token),
            failureCallback: (err: any) => {
              console.warn('Failed to get Teams auth token:', err);
              reject(err);
            },
            resources: [`api://${eeEnvVariables.RESOURCE_URI}/${eeEnvVariables.AUTH_CLIENT_ID}`],
          });
        });
      } catch (teamsTokenError) {
        console.log('Failed to get Teams auth token:', teamsTokenError);
      }

      const tokenForIDP = teamsToken || result?.idToken;

      if (!tokenForIDP) {
        console.error('No token available for IDP call');
        teams.authentication.notifyFailure('No authentication token available');
        return;
      }

      const idpConfigResponse = await axiosGetIDPConfig(tokenForIDP);
      const accessToken = idpConfigResponse?.access_token;
      const decodedToken = getDecodedB2cToken(accessToken);
      if (!decodedToken || typeof decodedToken !== 'object') {
        console.error('Failed to decode token or invalid token structure');
        teams.authentication.notifyFailure('Invalid token structure');
        return;
      }
      const realmRoles: string[] = decodedToken?.realm_access?.roles || [];
      const allRolesUpper = [...realmRoles]
        .filter(Boolean)
        .map((r: any) => String(r).toUpperCase());

      const hiringManagerRoleUpper = KFONE_ROLE.HIRING_MANAGER.toUpperCase();
      const isHiringManager = allRolesUpper.includes(hiringManagerRoleUpper);

      if (isHiringManager) {
        const claims = result?.account?.idTokenClaims || {};
        const email = result?.account?.username || claims?.preferred_username;
        const payload = {
          email,
          token: result?.accessToken,
        };
        teams.authentication.notifySuccess(JSON.stringify(payload));
      } else {
        console.error('User does not have hiring manager role. Email will NOT be sent.');
        teams.authentication.notifyFailure('User does not have hiring manager role');
        return;
      }
    } catch (err: any) {
      console.error('Error in sendUserDetailsToBot:', err);
      const errorMessage = err.message || 'Authentication failed';
      const payload = {
        email: errorMessage,
        token: result?.accessToken || null,
      };

      teams.authentication.notifySuccess(JSON.stringify(payload));
    }
  };
  return <div>Starting login…</div>;
};

export default BotAuthStart;
